import type { IMediaError } from '|>/shared/media-error'

export interface SmartRetryError {
  errorCode: string // matches error.code string from pureya's beforeerror hook

  /**
   * True when the current source is played in native mode (browser handles
   * playback directly, no MSE/VHS pipeline). Native streams skip automatic
   * retries and go straight to ERROR (CR-404 AC-9 / step 1.2).
   */
  isNative?: boolean
}

/** Player error object returned by pureya's `player.error()` after the beforeerror hook. */
export type PureyaPlayerError = IMediaError

/** Maps a pureya player error to the Smart-Restart domain type. */
export function toSmartRetryError(
  error: PureyaPlayerError | null | undefined
): SmartRetryError | null {
  if (error?.code == null) {
    return null
  }

  return { errorCode: String(error.code) }
}

/**
 * Lifecycle steps driven by the Smart-Restart recovery flow.
 * Naming follows the CR-404 player states.
 *
 * PLAYING  — normal playback or idle (no active recovery).
 * RETRYING — automatic recovery in progress.
 * ERROR    — auto-recovery exhausted; Retry button shown.
 */
export type SmartRestartStep = 'PLAYING' | 'RETRYING' | 'ERROR'

export type SmartRestartControllerConfig = {
  /** Is current stream DRM protected. Affects the max retry limit. */
  isDrm: boolean

  /**
   * Whether the current DRM stream's key systems are supported by the platform.
   * When false, an unsupported DRM stream skips automatic retries and the Retry
   * button (CR-404 AC-6). Ignored for non-DRM streams. Defaults to true.
   */
  isDrmSupported?: boolean

  /** Platform action to restart playback; adapter calls handlePlaybackRestored() or handleError(). */
  onRetry: () => void

  /** Callback invoked on each lifecycle step change. */
  onStepChange: (step: SmartRestartStep) => void

  /**
   * Called when a no-retry error aborts an in-flight recovery session
   * (FP-4176). Clears the Smart-Restart spinner and shows the plain error
   * overlay without the Retry button.
   */
  onRecoveryAbort?: () => void
}
